import type { Assessment } from "../assessment-types";

export const SELF_WORTH_ASSESSMENT: Assessment = {
  slug: "self-worth",
  emoji: "🪞",
  title: "Self-Worth & Shame Patterns",
  tagline: "The voice you use with yourself",
  intro:
    "This reflects on how you tend to speak to and about yourself, and how shame or self-criticism might show up day to day. There is no scoring for who you are, only patterns to notice with kindness.",
  questions: [
    { id: "q1", text: "Do you find yourself speaking to yourself more harshly than you would speak to a friend?" },
    { id: "q2", text: "Do you feel like a fundamentally flawed or broken person?" },
    { id: "q3", text: "Do you struggle to accept a compliment without brushing it off?" },
    { id: "q4", text: "Do you replay mistakes over and over, feeling ashamed long after they happened?" },
    { id: "q5", text: "Do you feel you have to achieve or perform to be worthy of love?" },
    { id: "q6", text: "Do you hide parts of yourself out of fear that others would judge you?" },
    { id: "q7", text: "Do you feel undeserving of good things happening to you?" },
    { id: "q8", text: "Do you find it hard to set boundaries because you feel you do not deserve them?" },
    { id: "q9", text: "Do you compare yourself unfavourably to almost everyone around you?" },
    { id: "q10", text: "Do you feel a wave of shame when you make even a small error?" },
    { id: "q11", text: "Do you struggle to name things you like or value about yourself?" },
    { id: "q12", text: "Do you feel like you need to apologise for taking up space or having needs?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest a fairly steady sense of self-worth.",
      body:
        "Self-criticism and shame do not seem to be dominant voices for you at the moment. Everyone has wobbles, and this looks like a workable relationship with yourself.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some patterns related to shame or self-criticism.",
      body:
        "There may be moments when an inner critical voice gets louder, or when you doubt your own worth. This voice often develops as a way of trying to stay safe or acceptable, and it can be gently questioned over time.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a recurring pattern of shame and self-criticism.",
      body:
        "It sounds like a harsh inner voice or a sense of not being enough shows up quite often. This pattern is commonly linked to earlier experiences where love or safety felt conditional, and it is a learned response rather than the truth about you.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong and frequent pattern of shame and self-criticism.",
      body:
        "It seems like feelings of unworthiness or a harsh inner critic are present much of the time. This kind of shame usually formed to help you cope with difficult circumstances, and with support it can soften considerably.",
    },
  ],
  next: [
    { label: "Meeting your inner critic", to: "/learn/inner-critic" },
    { label: "Reconnecting with your inner child", to: "/learn/inner-child" },
    { label: "Understanding people-pleasing", to: "/learn/people-pleasing" },
    { label: "Try a guided journal prompt", to: "/guided-journal" },
  ],
};
